import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

/**
 * Column mapping: template column index (0-based) → API field name
 *
 * Template headers (row 1):
 * A - ASIN (Pre-filled)                        → asin
 * B - SKU (Pre-filled)                         → sku
 * C - Title (Pre-filled)                       → item_name
 * D - Labels (to be filled by user)            → tags
 * E - CostPeriodStartDate (to be filled)       → item_cogs_batch_start_date
 * F - Cost (to be filled)                      → item_cogs
 * G - Value_of_unsellable_returns (to be filled) → unsellable_returns_percentage
 * H - VAT (to be filled)                       → vat_rate
 * I - Tax Paid on COGS (to be filled)          → product_tax_code
 * J - FBM Shipping Cost                        → fbm_shipping_cost_per_package
 * K - Replenishment Time (In Days)             → replenishment_time
 */
const COLUMN_MAP = [
    "asin",
    "sku",
    "item_name",
    "tags",
    "item_cogs_batch_start_date",
    "item_cogs",
    "unsellable_returns_percentage",
    "vat_rate",
    "product_tax_code",
    "fbm_shipping_cost_per_package",
    "replenishment_time",
];

/**
 * Loads the Excel template from the public folder, fills it with API records,
 * and triggers a download — all without blocking the UI.
 *
 * @param {Array}  records   - Array of product objects from get-products API
 * @param {string} fileName  - Output file name
 */
export const exportProductTemplate = async (records, fileName = "Product_Import_Template.xlsx") => {
    if (!records || records.length === 0) {
        console.warn("No records to fill into template");
        return;
    }

    // Load the template file from the public folder.
    // Place the template at: public/templates/Product_Import_Template.xlsx
    const templateUrl = `${window.location.origin}/assets/media/templates/Product_Import_Template.xlsx`;
    const res = await fetch(templateUrl);
    if (!res.ok) throw new Error(`Failed to load template: ${res.statusText}`);

    const arrayBuffer = await res.arrayBuffer();
    // cellStyles: true — required to read AND preserve existing styles from the template
    const workbook = XLSX.read(arrayBuffer, { type: "array", cellStyles: true });
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];

    // Decode the sheet range
    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    // Explicitly set bold on every header cell (row 0) so it survives the write pass
    for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (worksheet[cellAddress]) {
            worksheet[cellAddress].s = {
                ...(worksheet[cellAddress].s || {}),
                font: { bold: true },
            };
        }
    }

    // Fill data starting from row 2 (index 1), preserving row 1 headers exactly
    records.forEach((record, rowIndex) => {
        const excelRow = rowIndex + 2; // row 2 onwards (1-indexed, row 1 = headers)

        COLUMN_MAP.forEach((fieldName, colIndex) => {
            const cellAddress = XLSX.utils.encode_cell({ r: excelRow - 1, c: colIndex });
            const value = record[fieldName] ?? "";

            worksheet[cellAddress] = { v: value, t: typeof value === "number" ? "n" : "s" };
        });

        // Expand sheet range to include new rows
        if (excelRow - 1 > range.e.r) {
            range.e.r = excelRow - 1;
            worksheet["!ref"] = XLSX.utils.encode_range(range);
        }
    });

    // cellStyles: true — required on write to output the bold styles we set above
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array", cellStyles: true });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, fileName);
};